import React, { Fragment, useCallback, useEffect, useReducer, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import DataTable from "react-data-table-component";
import { useDebouncedCallback } from "use-debounce";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import shortenName from "../../helpers/shorten-name";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

const breedCell = (row) => {
  if (row.type === "Cat") return "-";
  return row.breed || "-";
};

const weightCell = (row) => row.weight ? `${row.weight} lbs` : "-";

const cratedCell = (row) => {
  if (row.type === "Dog") return row.isCrated ? "Yes" : "No";
  if (row.type === "Cat") return "Yes";
  return "-";
};

const columns = [
  {
    name: "Created At",
    cell: (row) => {
      return row.createdAt ? moment(row.createdAt).format("M/D/YY - h:mm a") : "";
    },
    width: "130px",
  },
  {
    name: "Leg",
    selector: "leg",
    cell: (row) => {
      const leg = row.leg;
      if (!leg) return "";

      return (
        <div>
          Leg {leg.legIndex !== -1 ? leg.legIndex + 1 : ""}: {moment(leg.departureDate).format("M/D/YY")} {leg.departureAirport?.code}-{leg.arrivalAirport?.code}
        </div>
      );
    },
    width: "180px",
  },
  {
    name: "Pet Name",
    selector: "name",
    cell: (row) => row.name,
    wrap: true,
    minWidth: "120px",
    maxWidth: "180px",
  },
  {
    name: "Type",
    selector: "type",
    cell: (row) => row.type,
    width: "80px",
  },
  {
    name: "Breed / Other",
    selector: "breed",
    cell: breedCell,
    wrap: true,
    minWidth: "150px",
    maxWidth: "220px",
  },
  {
    name: "Weight",
    selector: "weight",
    cell: weightCell,
    width: "90px",
  },
  {
    name: "Crated",
    selector: "isCrated",
    cell: cratedCell,
    width: "80px",
  },
  {
    name: "Status",
    selector: "status",
    cell: (row) => row.status === "approved" ? "Approved" : "Pending",
    width: "80px",
  },
  {
    name: "Added by",
    selector: "addedBy",
    minWidth: "80px",
    maxWidth: "120px",
    cell: (row) => row.addedBy ? shortenName(row.addedBy.name) : "",
  },
];

const conditionalRowStyles = [
  {
    when: (row) => row.status === "approved",
    style: {
      backgroundColor: "#9ce19b !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#247523 !important",
      },
    },
  },
];

const isRowDisabled = (row) => row.status === "approved" || !row.leg;

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
        selectedRows: [],
        clearSelectedRows: !state.clearSelectedRows,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: (action?.payload?.data?.data || []).map((item) => ({
          ...item,
          leg: item.leg ? {
            ...item.leg,
            legIndex: action?.payload?.legs?.findIndex(l => l._id === item.leg._id),
          } : null,
        })),
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  data: [],
  totalLength: 0,
  countPerPage: 5,
  currentPage: 1,
  selectedRows: [],
  clearSelectedRows: false,
  isSaving: false,
  refreshDataToken: Date.now(),
};

const PetRequestsTableModule = ({ tripData, readOnly }) => {
  const { id } = useParams();

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const getData = useCallback(async (params) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    dispatch({ type: "dataBeganDownloading" });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);

      const { data } = await ai.auth(token).get(`/api/trips/${id}/pet-requests`, {
        signal,
        params,
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
          legs: tripData?.itineraryDetails?.legs || [],
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  }, [getAccessTokenSilently, loginWithRedirect, id, tripData]);

  const debouncedGetData = useDebouncedCallback(
    (params) => {
      getData(params);
    },
    500,
  );

  useEffect(() => {
    debouncedGetData({
      page: state.currentPage,
      countPerPage: state.countPerPage,
    });
  }, [
    debouncedGetData,
    state.currentPage,
    state.countPerPage,
    state.refreshDataToken,
  ]);

  const handleSelection = useCallback((rowData) => {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  }, []);

  const handlePageChange = useCallback((page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  }, []);

  const handleRowsPerPageChange = useCallback((currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  }, []);

  const handleApprove = useCallback(async () => {
    if (!state.selectedRows?.length) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch(loginWithRedirect);

    const response = await ai.auth(token)
      .post(`/api/trips/${id}/pet-requests`, {
        ids: state.selectedRows.map(({ _id }) => _id),
      })
      .catch((error) => {
        Sentry.captureException(error);
      });

    if (response?.data) {
      toast.success("Pet requests have been approved successfully");
      dispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });
  }, [getAccessTokenSilently, loginWithRedirect, state.selectedRows, id]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={
          <h1 className="page-title d-flex" data-testid="pet-requests-title">
            Pet Requests

            {!readOnly && (
              <button
                className="base-button icon-button icon-button-small evo-blue ml-2"
                data-testid="pet-requests-approve-button"
                onClick={handleApprove}
                title="Approve"
                disabled={!state.selectedRows?.length || state.isSaving}
              >
                <span className="material-icons">publish</span>
              </button>
            )}
          </h1>
        }
        extraClasses="compact-view"
      />

      <div
        data-testid="pet-requests-table"
        className="server-pagination-table-container trips-list-table-container data-table-compact-view shadow-none mt-2">
        <DataTable
          columns={columns}
          data={state.data}
          progressPending={state.isLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          fixedHeader
          conditionalRowStyles={conditionalRowStyles}

          onSelectedRowsChange={handleSelection}
          selectableRows={!readOnly}
          clearSelectedRows={state.clearSelectedRows}
          selectableRowDisabled={isRowDisabled}

          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10, 15]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
        />
      </div>
    </Fragment>
  );
};

export default PetRequestsTableModule;
